import { useEffect, useState } from 'react';
import { ExternalLink, Filter, Search } from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { EmptyState } from '@/components/ui/EmptyState';
import { useCurrentWorkspace } from '@/lib/workspace-context';
import { useOutreachAgents, useOutreachLeads } from './api';
import { leadStatusMeta, type LeadStatus } from './mockData';

const PAGE_SIZE = 25;

export default function OutreachLeadsPage() {
  const { currentWorkspace } = useCurrentWorkspace();
  const wsId = currentWorkspace?.id;
  const [search, setSearch] = useState('');
  const [agentId, setAgentId] = useState('all');
  const [status, setStatus] = useState<LeadStatus | 'all'>('all');
  const [offset, setOffset] = useState(0);
  const leadsQuery = useOutreachLeads(wsId, {
    q: search.trim() || undefined,
    agentId: agentId === 'all' ? undefined : agentId,
    status: status === 'all' ? undefined : status,
    limit: PAGE_SIZE,
    offset,
  });
  const agentsQuery = useOutreachAgents(wsId, { limit: 200, offset: 0 });
  const leads = leadsQuery.data?.items ?? [];
  const agents = agentsQuery.data?.items ?? [];
  const total = leadsQuery.data?.total ?? leads.length;
  const currentPage = Math.floor(offset / PAGE_SIZE) + 1;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  useEffect(() => {
    setOffset(0);
  }, [agentId, search, status]);

  if (!wsId) {
    return <div className="p-8 text-gray-500">Selectionne un workspace pour voir les leads.</div>;
  }

  return (
    <div>
      <PageHeader
        title="Leads Outreach"
        description="Pipeline LinkedIn centralise"
        action={
          <Button variant="secondary" disabled>
            <Filter size={14} /> Filtres
          </Button>
        }
      />

      <Card className="mb-4 p-4">
        <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_auto] gap-3">
          <label className="relative block">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              className="input pl-9"
              placeholder="Rechercher par nom, entreprise, role..."
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
          </label>
          <select className="input md:w-48" value={agentId} onChange={(event) => setAgentId(event.target.value)}>
            <option value="all">Tous les agents</option>
            {agents.map((agent) => (
              <option key={agent.id} value={agent.id}>
                {agent.name}
              </option>
            ))}
          </select>
          <select
            className="input md:w-48"
            value={status}
            onChange={(event) => setStatus(event.target.value as LeadStatus | 'all')}
          >
            <option value="all">Tous les statuts</option>
            <option value="discovered">Decouvert</option>
            <option value="qualified">Qualifie</option>
            <option value="connected">Invitation</option>
            <option value="accepted">Accepte</option>
            <option value="followed_up">Relance</option>
            <option value="attention">Attention</option>
          </select>
        </div>
      </Card>

      <Card>
        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead>
              <tr className="border-b border-brand-border">
                <th className="th">Lead</th>
                <th className="th">Entreprise</th>
                <th className="th">Localisation</th>
                <th className="th">Agent</th>
                <th className="th">Campagne</th>
                <th className="th">Score</th>
                <th className="th">Statut</th>
                <th className="th">Dernier evenement</th>
                <th className="th">Profil</th>
              </tr>
            </thead>
            <tbody>
              {leads.map((lead) => {
                const leadStatus = leadStatusMeta[lead.status];
                return (
                  <tr key={lead.id} className="border-b border-brand-border table-row-hover">
                    <td className="td">
                      <div className="font-semibold text-brand-dark">{lead.full_name}</div>
                      <div className="text-xs text-gray-400">{lead.role_title ?? '-'}</div>
                    </td>
                    <td className="td">{lead.company ?? '-'}</td>
                    <td className="td">{lead.location ?? '-'}</td>
                    <td className="td">{lead.agent_name ?? '-'}</td>
                    <td className="td max-w-[220px] truncate">{lead.campaign_name ?? '-'}</td>
                    <td className="td">{lead.score}</td>
                    <td className="td"><Badge tone={leadStatus.tone}>{leadStatus.label}</Badge></td>
                    <td className="td">{lead.last_event ?? '-'}</td>
                    <td className="td">
                      {lead.profile_url ? (
                        <a
                          href={lead.profile_url}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center gap-1 text-brand-primary hover:underline"
                        >
                          Ouvrir <ExternalLink size={12} />
                        </a>
                      ) : (
                        '-'
                      )}
                    </td>
                  </tr>
                );
              })}
              {!leadsQuery.isLoading && leads.length === 0 && (
                <tr>
                  <td colSpan={9}>
                    <EmptyState
                      title="Aucun lead Outreach"
                      description="Les leads apparaitront apres la sync OpenOutreach."
                    />
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        {total > PAGE_SIZE && (
          <div className="flex flex-col gap-3 border-t border-brand-border px-4 py-3 text-sm text-gray-500 sm:flex-row sm:items-center sm:justify-between">
            <span>
              Page {currentPage} sur {totalPages} - {total} leads
            </span>
            <div className="flex items-center gap-2">
              <Button
                variant="secondary"
                size="sm"
                disabled={offset === 0}
                onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
              >
                Precedent
              </Button>
              <Button
                variant="secondary"
                size="sm"
                disabled={offset + PAGE_SIZE >= total}
                onClick={() => setOffset(offset + PAGE_SIZE)}
              >
                Suivant
              </Button>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}
